'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useAuthStore } from '@/stores/authStore';
import { isProfileModalExempt } from '@/lib/profileCompletion';
import ProfileCompletionModal from './ProfileCompletionModal';

interface ProfileCompletionWrapperProps {
  children: React.ReactNode;
}

export default function ProfileCompletionWrapper({ children }: ProfileCompletionWrapperProps) {
  const { isAuthenticated, isProfileComplete: profileComplete } = useAuthStore();
  const pathname = usePathname() ?? '';
  // Planner pages ask only for a name; the booking wizard asks for the profile before the order (isProfileModalExempt).
  const isProfileComplete = profileComplete || isProfileModalExempt(pathname);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Show modal when authenticated and profile is incomplete
    if (isAuthenticated && !isProfileComplete) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [isAuthenticated, isProfileComplete]);

  // Prevent navigation when modal is open
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (showModal) {
        e.preventDefault();
        e.returnValue = 'يجب إكمال الملف الشخصي قبل المغادرة';
        return 'يجب إكمال الملف الشخصي قبل المغادرة';
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      if (showModal) {
        e.preventDefault();
        window.history.pushState(null, '', window.location.href);
      }
    };

    if (showModal) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
      // Push current state to prevent back navigation
      window.history.pushState(null, '', window.location.href);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showModal]);

  const handleModalClose = () => {
    setShowModal(false);
  };

  // If user is not authenticated, show children normally
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // If user is authenticated but profile is not complete, show modal
  if (isAuthenticated && !isProfileComplete) {
    return (
      <>
        {children}
        <ProfileCompletionModal 
          isOpen={showModal} 
          onClose={handleModalClose} 
        />
      </>
    );
  }

  // If user is authenticated and profile is complete, show children normally
  return <>{children}</>;
}
